import axios from 'axios';
import { getFullHttpUrl } from '../firebase.config';
import authHeaders from './authHeaders';

// Referral program between firms (functions Referrals/*). The browser only
// remembers which link someone came from; the server decides everything
// else and keeps the referral when the team is created.

const STORAGE_KEY = 'referralCode';
const KEEP_DAYS = 30;

export const normalizeCode = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40);

// Remembers the code from /r/<code> or ?ref=<code> for 30 days.
export const rememberReferral = (code) => {
  const clean = normalizeCode(code);
  if (!clean) {
    return null;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: clean, until: Date.now() + KEEP_DAYS * 24 * 60 * 60 * 1000 }));
  } catch (e) {
    // Private browsing: the code still travels in the URL to the signup form.
  }
  return clean;
};

export const storedReferral = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && saved.code && saved.until > Date.now()) {
      return saved.code;
    }
  } catch (e) {
    return null;
  }
  return null;
};

export const forgetReferral = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // nothing to do
  }
};

// ?ref=<code> on any page.
export const captureReferralFromUrl = () => {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    return code ? rememberReferral(code) : null;
  } catch (e) {
    return null;
  }
};

const call = async (name, data, signed = true) => {
  const res = await axios.post(getFullHttpUrl(`referrals-${name}`), data, signed ? { headers: await authHeaders() } : undefined);
  return res.data;
};

export const getMyProgram = () => call('myProgram', {});
// Sends the team's link to someone at another firm (referrals-invite):
// { sent: true } or { sent: false, reason: 'already-invited', invitedAt }.
export const inviteByEmail = (email) => call('invite', { email });
export const getCodeInfo = (code) => call('codeInfo', { code }, false);
export const officeReferralAction = (id, action, note = '') => call('officeAction', { id, action, note });

export const formatDollars = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// What the signup keeps from referrals-codeInfo: the code, the firm and the
// deal. A server from before 2026-10-07 sends no welcomeRule: its deal was
// the credit after the first paid order.
export const referralFrom = (info) => ({
  code: info.code,
  firmName: info.firmName,
  welcomeRule: info.welcomeRule || (info.welcomeCents ? 'first-order' : 'threshold'),
  welcomeCents: info.welcomeCents,
  thresholdCents: info.thresholdCents,
  windowDays: info.windowDays,
});

// The line under "<firm> invited you" on the signup. t: its translator.
export const referralBenefit = (referral, t) => {
  if (referral.welcomeRule === 'first-order') {
    return t('account.create.referralBenefit', { amount: formatDollars(referral.welcomeCents || 10000) });
  }
  return t('account.create.referralBenefitBoth', {
    amount: formatDollars(referral.welcomeCents || 25000),
    threshold: formatDollars(referral.thresholdCents || 50000),
    days: referral.windowDays || 90,
  });
};

export const shortDate = (ms) => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

// Colors of a referral's status, the same for the team and office.
export const STATUS_CLASS = {
  signed_up: 'refPillSigned',
  first_paid: 'refPillPaid',
  qualified: 'refPillPending',
  credited: 'refPillCredited',
  void: 'refPillVoid',
};

// What went wrong with an email invite, for the person who sent it.
//   words: the team's kind of organization (utility/orgType orgWords)
export const inviteErrorMessage = (error, words = { one: 'firm', plural: 'firms' }) => {
  const status = error && error.response && error.response.status;
  switch (error && error.response && error.response.data) {
    case 'invalid-email':
      return "That doesn't look like an email address. Please, check it.";
    case 'own-email':
      return `That's your own email. Invite someone at another ${words.one}.`;
    case 'same-domain':
      return `That address is on your own email domain. Referrals are for other ${words.plural}.`;
    case 'too-many-requests':
      return "You've sent a lot of invites today. Please, try again tomorrow.";
    case 'team-inactive':
      return "Your team's account isn't active, so it can't send invites.";
    case 'no-team':
      return 'Invites are for firm accounts.';
    case 'invite-not-sent':
      return "We couldn't send the invite. Please, try again in a few minutes.";
    default:
      return status === 429
        ? "You've sent a lot of invites today. Please, try again tomorrow."
        : "We couldn't send the invite. Please, try again or refresh the page.";
  }
};

// The answer when that address was already invited in the last 30 days.
export const alreadyInvitedMessage = (email, invitedAt) =>
  `You already invited ${email}${invitedAt ? ` on ${shortDate(invitedAt)}` : ''}. You can send it again 30 days after that.`;

export const referralErrorMessage = (error) => {
  switch (error && error.response && error.response.data) {
    case 'already-credited':
      return 'That credit was already given.';
    case 'already-void':
      return 'This referral was already voided.';
    case 'changed-meanwhile':
      return 'The referral changed meanwhile. Please, refresh and try again.';
    case 'not-allowed':
      return "You don't have permission to do this.";
    default:
      return 'There was an error. Please, try again or refresh the page.';
  }
};
